
import React, { useState } from 'react';
import './BillingPage.css';
import PageHeader from '../../components/shared/PageHeader';
import Card from '../../components/shared/Card';
import Button from '../../components/shared/Button';
import Badge from '../../components/shared/Badge';
import { useToast } from '../../context/ToastContext';
import InvoiceGenerator from '../invoices/InvoiceGenerator';

const INVOICES = [
  { id: 'INV-0047', client: 'Acme Corp', amount: '$4,200', status: 'Paid', date: 'Feb 18, 2026', due: 'Feb 25, 2026' },
  { id: 'INV-0048', client: 'BlueSky Inc', amount: '$1,800', status: 'Pending', date: 'Feb 20, 2026', due: 'Mar 5, 2026' },
  { id: 'INV-0049', client: 'Meridian Labs', amount: '$7,500', status: 'Overdue', date: 'Jan 30, 2026', due: 'Feb 14, 2026' },
  { id: 'INV-0050', client: 'Synapse AI', amount: '$3,100', status: 'Draft', date: 'Feb 23, 2026', due: 'Mar 10, 2026' },
  { id: 'INV-0051', client: 'Orbit Software', amount: '$950', status: 'Paid', date: 'Feb 10, 2026', due: 'Feb 24, 2026' },
];
const PROPOSALS = [
  { id: 'PRO-001', title: 'Enterprise SaaS Solution', client: 'Acme Corp', value: '$48,000', status: 'Sent' },
  { id: 'PRO-002', title: 'AI Integration Package', client: 'Synapse AI', value: '$82,000', status: 'Accepted' },
  { id: 'PRO-003', title: 'Data Analytics Platform', client: 'Meridian Labs', value: '$34,500', status: 'Draft' },
];
const STATUS_VARIANT: Record<string, any> = { Paid: 'success', Pending: 'warning', Overdue: 'danger', Draft: 'default', Sent: 'warning', Accepted: 'success', Rejected: 'danger' };

export default function BillingPage() {
  const { addToast } = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState<'invoices' | 'proposals' | 'generator'>('invoices');
  const [form, setForm] = useState({ client: '', amount: '', desc: '', due: '' });

  return (
    <div className="billing-page">
      <PageHeader
        title="Money"
        breadcrumb={['Business', 'Money']}
        actions={<Button icon="+" size="sm" onClick={() => setShowCreate(true)}>{tab === 'invoices' ? 'Create Invoice' : 'New Proposal'}</Button>}
      />
      <div className="billing-stats">
        {[
          { label: 'Total Revenue', value: '$48,291', icon: '💰', variant: 'success' },
          { label: 'Outstanding', value: '$9,300', icon: '⏳', variant: 'warning' },
          { label: 'Overdue', value: '$7,500', icon: '⚠', variant: 'danger' },
          { label: 'Paid (MTD)', value: '$31,491', icon: '✅', variant: 'ocean' },
        ].map(s => (
          <div key={s.label} className={`billing-stat billing-stat--${s.variant}`}>
            <span className="billing-stat__icon">{s.icon}</span>
            <span className="billing-stat__val">{s.value}</span>
            <span className="billing-stat__label">{s.label}</span>
          </div>
        ))}
      </div>
      <div className="billing-tabs">
        <button className={`billing-tab${tab === 'invoices' ? ' billing-tab--active' : ''}`} onClick={() => setTab('invoices')}>Invoices</button>
        <button className={`billing-tab${tab === 'proposals' ? ' billing-tab--active' : ''}`} onClick={() => setTab('proposals')}>Proposals</button>
        <button className={`billing-tab${tab === 'generator' ? ' billing-tab--active' : ''}`} onClick={() => setTab('generator')}>Generator</button>
      </div>
      {tab === 'invoices' && (
        <Card noPad>
          <div className="billing-table-wrap">
            <table className="billing-table">
              <thead><tr><th>Invoice</th><th>Client</th><th>Amount</th><th>Status</th><th>Date</th><th>Due</th><th>Actions</th></tr></thead>
              <tbody>
                {INVOICES.map(inv => (
                  <tr key={inv.id}>
                    <td className="billing-id">{inv.id}</td>
                    <td>{inv.client}</td>
                    <td className="billing-amount">{inv.amount}</td>
                    <td><Badge variant={STATUS_VARIANT[inv.status]}>{inv.status}</Badge></td>
                    <td className="billing-muted">{inv.date}</td>
                    <td className="billing-muted">{inv.due}</td>
                    <td>
                      <div className="billing-actions">
                        <button className="crm-act-btn" title="Download">⬇</button>
                        <button className="crm-act-btn" title="Send">📤</button>
                        <button className="crm-act-btn" title="Edit">✏</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {tab === 'proposals' && (
        <Card noPad>
          <div className="billing-table-wrap">
            <table className="billing-table">
              <thead><tr><th>ID</th><th>Title</th><th>Client</th><th>Value</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {PROPOSALS.map(p => (
                  <tr key={p.id}>
                    <td className="billing-id">{p.id}</td>
                    <td style={{ fontWeight: 500 }}>{p.title}</td>
                    <td className="billing-muted">{p.client}</td>
                    <td style={{ fontWeight: 700 }}>{p.value}</td>
                    <td><Badge variant={STATUS_VARIANT[p.status]}>{p.status}</Badge></td>
                    <td><div className="billing-actions"><button className="crm-act-btn">👁</button><button className="crm-act-btn">✏</button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {tab === 'generator' && <InvoiceGenerator />}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>{tab === 'invoices' ? 'Create Invoice' : 'New Proposal'}</h3>
              <button className="modal__close" onClick={() => setShowCreate(false)}>✕</button>
            </div>
            <div className="modal__body">
              <label className="form-label">Client Name</label>
              <input className="form-input" value={form.client} onChange={e => setForm(f => ({...f, client: e.target.value}))} placeholder="Acme Corp" />
              <label className="form-label">Amount</label>
              <input className="form-input" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} placeholder="$0.00" />
              <label className="form-label">Description</label>
              <textarea className="form-input form-textarea" value={form.desc} onChange={e => setForm(f => ({...f, desc: e.target.value}))} placeholder="Services rendered…" />
              <label className="form-label">Due Date</label>
              <input className="form-input" type="date" value={form.due} onChange={e => setForm(f => ({...f, due: e.target.value}))} />
            </div>
            <div className="modal__footer">
              <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={() => { addToast('success', `Created for ${form.client || 'client'}!`); setShowCreate(false); }}>Create</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
